import type { Journey, JourneyNode } from './journeyTiers';
import { formatPartyLabel } from './phone';
import { strings } from '../strings/en';

/**
 * Text for a journey node in a narrative sentence. A phone-bearing node
 * ('external' / 'person') combines its caller-ID name with its formatted
 * number, e.g. "TERRY FOX FDTN (450) 923-8950" (owner morning review G3);
 * every other node kind is unchanged (`node.name` for a resource, else
 * `node.label`).
 */
function partyText(node: JourneyNode): string {
  if (node.number) {
    return formatPartyLabel(node.name, node.number);
  }
  return node.name ?? node.label;
}

/** `m:ss`, e.g. "0:41", "3:24" -- the same vocabulary as the journey diagram's segment labels. */
function mmss(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return strings.interaction.segmentDuration(m, s);
}

/**
 * One sentence for the journey (spec §6). Owner send-back F5d: this must
 * never name infrastructure (SBC, carrier, Teams, signal group) for a
 * non-technical user -- carrier/sbc/teams nodes are skipped outright below,
 * and (with the F5c fix in `journeyTiers.ts`) only exist in `j.nodes` for a
 * real, un-emulated platform admin in the first place.
 * Inbound example: "(807) 220-1470 called the main line, reached the
 * attendant, waited in the queue 0:41, and was answered by Audrey Debruyne
 * (3:24)." -- with whatever hops exist.
 */
export function journeyNarrative(j: Journey): string {
  if (j.nodes.length === 0) return strings.narrative.unknownRoute;
  const n = strings.narrative;
  const parts: string[] = [];
  const first = j.nodes[0];
  const outbound = first.kind === 'you';
  // "it rang you" only makes sense when something routed the call first. A direct
  // call has no main line, attendant or queue, so the phrase is dropped entirely.
  const routed = j.nodes.some((x) => x.kind === 'main_line' || x.kind === 'auto_attendant' || x.kind === 'call_queue');
  const onMainLine = j.nodes.some((x) => x.kind === 'main_line');
  parts.push(
    outbound
      ? n.outboundStart(partyText(j.nodes[j.nodes.length - 1]))
      : onMainLine
        ? n.inboundStartOnMainLine(partyText(first))
        : n.inboundStart(partyText(first)),
  );

  for (let i = 1; i < j.nodes.length; i++) {
    const node = j.nodes[i];
    const seg = j.segments.find((s) => s.toId === node.id);
    const name = partyText(node);
    const duration = seg?.durationSeconds != null ? mmss(seg.durationSeconds) : null;
    switch (node.kind) {
      case 'carrier': case 'sbc': case 'teams': case 'main_line': break;
      case 'auto_attendant': parts.push(n.reachedAttendant(node.name)); break;
      case 'call_queue': parts.push(n.waitedInQueue(node.name, duration)); break;
      case 'voicemail': parts.push(n.wentToVoicemail); break;
      // Outbound's terminal node is the party dialled -- its own clause
      // (answered/no-answer), not the routed "it rang you" lead-in.
      case 'external': {
        if (!outbound) break;
        if (seg?.outcome === 'answered') parts.push(duration ? n.ended(duration) : n.endedNoDuration);
        else if (seg?.outcome === 'no_answer' || seg?.outcome === 'missed' || seg?.outcome === 'failed') parts.push(n.notAnswered);
        break;
      }
      case 'you': case 'person': {
        if (outbound) break;
        if (seg?.outcome === 'answered') {
          parts.push(node.kind === 'you' ? n.youAnsweredWithDuration(duration) : n.answeredByWithDuration(name, duration));
        } else if (seg?.outcome === 'transferred') {
          if (routed) parts.push(node.kind === 'you' ? n.rangYou : n.rangPerson(name));
          parts.push(n.transferredTo(name));
        } else if (seg?.outcome === 'no_answer' || seg?.outcome === 'missed' || seg?.outcome === 'failed') {
          if (routed) parts.push(node.kind === 'you' ? n.rangYou : n.rangPerson(name));
          parts.push(n.notAnswered);
        }
        break;
      }
      default: break;
    }
  }
  // Oxford "and" before the final clause once there are enough hops to read
  // as a list (spec send-back F5d example: "..., waited in the queue 0:41,
  // and was answered by Audrey Debruyne (3:24)."). A two-clause sentence
  // ("X called, ended after 0:42.") reads fine without it.
  if (parts.length >= 3) {
    const head = parts.slice(0, -1).join(', ');
    const tail = parts[parts.length - 1];
    return `${head}, and ${tail}.`;
  }
  return `${parts.join(', ')}.`;
}
